import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '@carrent/api'
import { DeltaStat, Icon, ImageCompare, PageHeader, StatusBadge } from '@carrent/ui'

/**
 * Что я сдал: своя поездка глазами водителя.
 *
 * ── Почему это не тот же экран, что у диспетчера ──────────────────
 *
 * Соблазн — отдать водителю консольное сравнение, только поуже.
 * Но задачи разные. Диспетчер решает, принимать поездку; водитель
 * смотрит, что он вообще сдал: сходится ли пробег, все ли снимки
 * дошли. Поэтому здесь нет ни флагов аномалий, ни действий,
 * ни двух колонок — только собственный акт и посчитанная разница.
 *
 * ── Флаги не прячутся в интерфейсе, их просто нет ─────────────────
 *
 * Сервер водителю ключ flags не отдаёт вовсе, и здесь нет даже
 * блока «замечаний нет». Такой блок означал бы, что флаги водителю
 * иногда показывают, — и первый же случай, когда диспетчер видит
 * замечание, а водитель «чисто», превратился бы в спор о том,
 * кто что скрыл. Инструмент контроля не должен выглядеть
 * инструментом самопроверки.
 *
 * ── Пары идут одна под другой ─────────────────────────────────────
 *
 * Две колонки по половине телефона превращают царапину в пиксель.
 * ImageCompare на узком экране раскладывает пару вертикально сам —
 * здесь ничего сжимать не нужно.
 */

function formatDuration(minutes) {
  if (minutes === null || minutes === undefined) return '—'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h ? `${h} ч ${m} мин` : `${m} мин`
}

function formatKm(value) {
  return value === null || value === undefined ? '—' : `${value.toLocaleString('ru')} км`
}

function formatTime(iso) {
  if (!iso) return null
  return new Date(iso).toLocaleString('ru', { dateStyle: 'short', timeStyle: 'short' })
}

function photoProps(photo) {
  if (!photo) return null
  return { url: photo.url, takenAt: photo.taken_at, takenAtLabel: formatTime(photo.taken_at) }
}

function Events({ events }) {
  if (!events.length) return null

  return (
    <>
      <p className="section-title">Что было в поездке</p>
      <ul className="trip-detail__events">
        {events.map((ev) => (
          <li key={ev.id} className="trip-detail__event">
            <span className="trip-detail__event-label">{ev.label}</span>
            <span className="trip-detail__event-figures">
              {ev.amount ? `${ev.amount.toLocaleString('ru')} ₽` : null}
            </span>
          </li>
        ))}
      </ul>
    </>
  )
}

export default function TripDetailPage() {
  const { id } = useParams()
  const [data, setData] = useState(null)
  const [failed, setFailed] = useState(false)

  const load = () => {
    setFailed(false)
    setData(null)
    api.get(`/trips/${id}/comparison`)
      .then(({ data: body }) => setData(body.data))
      .catch(() => setFailed(true))
  }
  useEffect(load, [id])

  if (failed) {
    return (
      <div>
        <PageHeader title="Поездка" backTo="/trips" />
        <div className="p-page state">
          <p className="state__title">Поездка не открылась</p>
          <p className="state__text">Проверьте связь и попробуйте ещё раз.</p>
          <button type="button" className="btn btn--secondary btn--full" onClick={load}>
            Повторить
          </button>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div>
        <PageHeader title="Поездка" backTo="/trips" />
        <div className="full-page-center" role="status">
          <span className="spinner" />
        </div>
      </div>
    )
  }

  const { odometer, fuel, pairs, start_only: startOnly } = data
  const extraShots = startOnly.filter((s) => s.photo)

  return (
    <div className="trip-detail">
      <PageHeader
        title="Моя поездка"
        backTo="/trips"
        right={<StatusBadge status={data.status} />}
      />

      <div className="p-page flex-col">
        {data.partial && (
          <div className="alert alert--info">
            Поездка ещё идёт — здесь пока только то, что вы сдали при выезде.
          </div>
        )}

        <div className="trip-detail__deltas">
          <DeltaStat
            label="Проехали"
            value={formatKm(odometer.delta)}
            hint={`${formatKm(odometer.start)} → ${formatKm(odometer.end)}`}
          />
          <DeltaStat
            label="В пути"
            value={formatDuration(data.duration_minutes)}
          />
          <DeltaStat
            label="Заправки"
            value={fuel.refuel_count ? `${fuel.liters} л` : 'нет'}
            hint={fuel.refuel_count
              ? `${fuel.refuel_count} шт · ${fuel.amount?.toLocaleString('ru')} ₽`
              : null}
          />
        </div>

        <Events events={data.events} />

        <p className="section-title">Ваши снимки: выезд и возврат</p>
        <p className="trip-detail__note">
          <Icon name="camera" size={14} />
          Это то, что приняла система. Если снимка нет — он не дошёл.
        </p>

        <div className="trip-detail__pairs">
          {pairs.map((pair) => (
            <ImageCompare
              key={pair.category}
              title={pair.label}
              beforeLabel="При выезде"
              afterLabel="При возврате"
              before={photoProps(pair.start_photo)}
              after={photoProps(pair.end_photo)}
            />
          ))}
        </div>

        {extraShots.length > 0 && (
          <>
            <p className="section-title">Снимали только при выезде</p>
            <div className="trip-detail__extras">
              {extraShots.map((s) => (
                <figure key={s.category} className="trip-detail__extra">
                  <img src={s.photo.url} alt={s.label} loading="lazy" />
                  <figcaption>{s.label}</figcaption>
                </figure>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
